import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { useLanguage } from "../i18n/LanguageContext";
import LangSwitch from "./LangSwitch";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import "./SiteNav.css";

interface Props {
  /** Over a photograph the bar starts transparent with light ink. */
  overHero?: boolean;
}

/**
 * The top bar. Transparent over the hero, then a blurred cream strip once
 * the page moves. On phones it opens a full-height espresso panel.
 */
export default function SiteNav({ overHero = false }: Props) {
  const { t } = useLanguage();
  const location = useLocation();
  const reduce = useReducedMotion();

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Route change closes the panel; the panel locks page scroll while open.
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const light = overHero && !scrolled && !open;

  const links = [
    { to: "/menu", label: t("navMenu") },
    { to: "/#house", label: t("navStory") },
    { to: "/#hours", label: t("navHours") },
  ];

  return (
    <>
      <a className="skip-link" href="#main">
        {t("skipToContent")}
      </a>

      <header className={`nav${scrolled ? " is-scrolled" : ""}${light ? " is-light" : ""}${open ? " is-open" : ""}`}>
        <div className="site-inner nav-inner">
          <Link to="/" className="nav-brand" aria-label={t("brand")}>
            <Logo tone={light || open ? "light" : "dark"} height={30} />
          </Link>

          <nav className="nav-links" aria-label="Primary">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `nav-link${isActive && !link.to.includes("#") ? " is-active" : ""}`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="nav-actions">
            <LangSwitch tone={light ? "light" : "dark"} />
            <ThemeToggle tone={light ? "light" : "dark"} />


            <Link to="/reserve" className="btn btn-primary btn-sm nav-reserve">
              {t("navReserve")}
            </Link>

            <button
              className="nav-burger"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="mobile-panel"
              aria-label={open ? t("close") : "Menu"}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-panel"
            className="nav-panel"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="site-inner nav-panel-inner">
              <ul className="nav-panel-links">
                {[{ to: "/", label: "dähääm" }, ...links, { to: "/reserve", label: t("navReserve") }].map((link, index) => (
                  <motion.li
                    key={link.to}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12 + index * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link to={link.to} className="nav-panel-link display">
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>

              <div className="nav-panel-foot">
                <div className="nav-panel-tools">
                  <LangSwitch tone="light" />
                  <ThemeToggle tone="light" />
                </div>
                <p className="tiny nav-panel-sub">{t("brandSub")} · {t("brandLine")}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

